import { Link, useForm } from "@inertiajs/react"
import { useState } from "react"

interface Option {
  id: number
  name: string
}

interface LogData {
  log_type_id: number | null
  customer_user_id: number | null
}

interface Props {
  customerId: number
  log: LogData
  log_types: Option[]
  contacts: Option[]
  employees: Option[]
  adminIds: number[]
  salesRepId: number | null
  errors?: Record<string, string[]>
}

export default function LogNoteForm({
  customerId,
  log,
  log_types,
  contacts,
  employees,
  adminIds,
  salesRepId,
  errors,
}: Props) {
  const [showTime, setShowTime] = useState(false)
  const [showExpense, setShowExpense] = useState(false)

  const defaultParticipantIds = [
    ...new Set([
      ...adminIds.filter((id) => employees.some((e) => e.id === id)),
      ...(salesRepId && employees.some((e) => e.id === salesRepId)
        ? [salesRepId]
        : []),
    ]),
  ]

  const { data, setData, post, processing, transform } = useForm({
    notes: "",
    log_type_id: log.log_type_id,
    customer_user_id: log.customer_user_id,
    participant_ids: defaultParticipantIds,
    time_hours: 0,
    time_minutes: 0,
    time_notes: "",
    expense_amount: "",
    expense_notes: "",
  })

  transform((data) => ({
    log: {
      notes: data.notes,
      log_type_id: data.log_type_id,
      customer_user_id: data.customer_user_id,
    },
    participant_ids: data.participant_ids,
    marketing_time: {
      hours: data.time_hours,
      minutes: data.time_minutes,
      notes: data.time_notes,
    },
    marketing_expense: {
      amount: data.expense_amount,
      notes: data.expense_notes,
    },
    type: "log_note",
  }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/customers/${customerId}/logs`)
  }

  const toggleParticipant = (id: number) => {
    setData(
      "participant_ids",
      data.participant_ids.includes(id)
        ? data.participant_ids.filter((pid) => pid !== id)
        : [...data.participant_ids, id],
    )
  }

  // Collect all error messages (field-level + base)
  const allErrors: string[] = []
  if (errors) {
    Object.values(errors).forEach((msgs) => {
      allErrors.push(...msgs)
    })
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-3xl">
      {allErrors.length > 0 && (
        <div className="bg-hpi-red/10 text-hpi-red mb-4 rounded p-3">
          {allErrors.map((msg, i) => (
            <p key={i}>{msg}</p>
          ))}
        </div>
      )}

      {/* Section 1: Log Note */}
      <div className="border-hpi-light-gray mb-6 rounded border p-4">
        <h2 className="mb-4 text-lg font-semibold">Add Log Note</h2>

        <div className="mb-4">
          <label htmlFor="log_type_id" className="mb-1 block font-medium">
            Contact Type
          </label>
          <select
            id="log_type_id"
            value={data.log_type_id ?? ""}
            onChange={(e) =>
              setData(
                "log_type_id",
                e.target.value ? parseInt(e.target.value) : null,
              )
            }
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          >
            <option value="">Select...</option>
            {log_types.map((lt) => (
              <option key={lt.id} value={lt.id}>
                {lt.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="customer_user_id" className="mb-1 block font-medium">
            Contact
          </label>
          <select
            id="customer_user_id"
            value={data.customer_user_id ?? ""}
            onChange={(e) =>
              setData(
                "customer_user_id",
                e.target.value ? parseInt(e.target.value) : null,
              )
            }
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
          >
            <option value="">Select...</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="notes" className="mb-1 block font-medium">
            Notes <span className="text-hpi-red">*</span>
          </label>
          <textarea
            id="notes"
            value={data.notes}
            onChange={(e) => setData("notes", e.target.value)}
            className="border-hpi-light-gray w-full rounded border px-3 py-2"
            rows={4}
            required
            placeholder="Please enter a log entry"
          />
        </div>

        {employees.length > 0 && (
          <div className="mb-4">
            <label className="mb-2 block font-medium">Notify</label>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 md:grid-cols-3">
              {employees.map((emp) => (
                <label key={emp.id} className="flex items-center gap-2 py-1">
                  <input
                    type="checkbox"
                    checked={data.participant_ids.includes(emp.id)}
                    onChange={() => toggleParticipant(emp.id)}
                    className="border-hpi-light-gray rounded"
                  />
                  <span className="text-sm">{emp.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Section 2: Time (optional, collapsible) */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => setShowTime(!showTime)}
          className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue mb-2 flex w-full items-center justify-between rounded border px-4 py-2 text-left font-medium"
        >
          <span>Add Time Entry (Optional)</span>
          <span className="text-lg">{showTime ? "\u2212" : "+"}</span>
        </button>

        {showTime && (
          <div className="border-hpi-light-gray rounded border p-4">
            <div className="mb-4 grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="time_hours" className="mb-1 block font-medium">
                  Hours
                </label>
                <select
                  id="time_hours"
                  value={data.time_hours}
                  onChange={(e) =>
                    setData("time_hours", parseInt(e.target.value))
                  }
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                >
                  {Array.from({ length: 13 }, (_, i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="time_minutes"
                  className="mb-1 block font-medium"
                >
                  Minutes
                </label>
                <select
                  id="time_minutes"
                  value={data.time_minutes}
                  onChange={(e) =>
                    setData("time_minutes", parseInt(e.target.value))
                  }
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                >
                  {[0, 15, 30, 45].map((m) => (
                    <option key={m} value={m}>
                      {m.toString().padStart(2, "0")}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="time_notes" className="mb-1 block font-medium">
                Notes
              </label>
              <textarea
                id="time_notes"
                value={data.time_notes}
                onChange={(e) => setData("time_notes", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                rows={2}
              />
            </div>
          </div>
        )}
      </div>

      {/* Section 3: Expense (optional, collapsible) */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => setShowExpense(!showExpense)}
          className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue mb-2 flex w-full items-center justify-between rounded border px-4 py-2 text-left font-medium"
        >
          <span>Add Expense (Optional)</span>
          <span className="text-lg">{showExpense ? "\u2212" : "+"}</span>
        </button>

        {showExpense && (
          <div className="border-hpi-light-gray rounded border p-4">
            <div className="mb-4">
              <label
                htmlFor="expense_amount"
                className="mb-1 block font-medium"
              >
                Amount
              </label>
              <input
                type="text"
                id="expense_amount"
                value={data.expense_amount}
                onChange={(e) => setData("expense_amount", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                placeholder="0.00"
              />
            </div>

            <div>
              <label htmlFor="expense_notes" className="mb-1 block font-medium">
                Notes{" "}
                <span className="text-hpi-med-gray text-sm font-normal">
                  (required if expense to be added)
                </span>
              </label>
              <textarea
                id="expense_notes"
                value={data.expense_notes}
                onChange={(e) => setData("expense_notes", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                rows={2}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          Save Log Note
        </button>
        <Link
          href={`/customers/${customerId}/logs`}
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
